import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatSAR, formatDate } from "@/lib/api";
import DateFilterChips from "@/components/DateFilterChips";
import PayoutReceipt from "@/components/PayoutReceipt";
import { RefreshCw, Wallet, FileText } from "lucide-react";

export default function TherapistEarningsPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [payouts, setPayouts] = useState([]);
  const [viewReceipt, setViewReceipt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    try {
      const [earn, pay] = await Promise.all([
        api.get("/reports/therapist/me", { params }),
        api.get("/therapists/me/payouts"),
      ]);
      setData(earn.data);
      setPayouts(pay.data.payouts || []);
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const periodLabel = from && to
    ? (from === to ? formatDate(from + "T00:00:00").split(",")[0] : `${from} → ${to}`)
    : t("ther.allTotal");

  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs uppercase tracking-[0.3em] text-gold">— Therapist</span>
        <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("ther.title")}</h1>
        <p className="text-foreground/50 mt-1 text-sm">Periode: <span className="text-foreground/80">{periodLabel}</span></p>
      </div>

      {/* Date filter chips */}
      <div className="card-luxury p-4 flex flex-wrap items-center justify-between gap-3">
        <DateFilterChips
          from={from}
          to={to}
          onChange={(f, tt) => { setFrom(f); setTo(tt); }}
          onClear={() => { setFrom(""); setTo(""); }}
        />
        <button onClick={load} className="p-2 rounded-full hover:bg-gold/10 text-gold">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!data && <div className="text-center py-10 text-foreground/40">{t("common.loading")}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card-luxury accent-gold p-5" data-testid="therapist-period-total">
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.totalEarned")}</div>
              <div className="mt-2 text-3xl font-heading text-gold">{formatSAR(data.all_total)}</div>
              <div className="text-[10px] text-foreground/40 mt-1">{data.session_count} {t("ther.sessions")}</div>
            </div>
            <div className="card-luxury accent-gold p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.monthTotal")}</div>
              <div className="mt-2 text-2xl font-heading">{formatSAR(data.month_total)}</div>
            </div>
            <div className="card-luxury accent-leaf p-5" data-testid="therapist-paid">
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.paid")}</div>
              <div className="mt-2 text-2xl font-heading text-leaf">{formatSAR(data.total_paid || 0)}</div>
            </div>
            <div className="card-luxury accent-gold p-5" data-testid="therapist-pending">
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.pending")}</div>
              <div className="mt-2 text-2xl font-heading text-gold">{formatSAR(data.pending_commission || 0)}</div>
            </div>
          </div>

          {/* Commission Payouts received */}
          <div className="card-luxury p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-leaf" />
                <div>
                  <h2 className="text-lg font-heading">{t("ther.payoutsTitle")}</h2>
                  <p className="text-xs text-foreground/50">{t("ther.payoutsSub")}</p>
                </div>
              </div>
              <div className="text-end">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.paid")}</div>
                <div className="text-xl font-heading text-leaf">
                  {formatSAR(payouts.reduce((s, p) => s + (p.amount || 0), 0))}
                </div>
              </div>
            </div>

            {payouts.length === 0 ? (
              <div className="text-center py-8 text-foreground/40 border border-dashed border-gold/15 rounded-lg">
                {t("ther.noPayouts")}
              </div>
            ) : (
              <div className="space-y-2" data-testid="therapist-payouts-list">
                {payouts.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`therapist-payout-${p.id}`}
                    className="flex items-center justify-between bg-charcoal-900/60 border border-leaf/15 rounded-lg p-3 hover:border-leaf/30 transition"
                  >
                    <div className="min-w-0">
                      <div className="font-heading text-leaf text-lg">{formatSAR(p.amount)}</div>
                      <div className="text-[11px] text-foreground/50 mt-0.5">
                        {formatDate(p.paid_at)} · {t("ther.paidBy")}: {p.paid_by_name || "—"}
                      </div>
                      {p.notes && (
                        <div className="text-xs text-foreground/70 mt-1 italic truncate max-w-xs">&ldquo;{p.notes}&rdquo;</div>
                      )}
                    </div>
                    <button
                      onClick={() => setViewReceipt(p)}
                      data-testid={`therapist-receipt-${p.id}`}
                      className="inline-flex items-center gap-1.5 text-xs btn-outline-gold py-2 px-3 shrink-0"
                    >
                      <FileText className="h-3.5 w-3.5" /> {t("receipt.payout.view")}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-luxury overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50 bg-charcoal-900/60">
                  <tr>
                    <th className="text-start px-4 py-3">{t("reports.date")}</th>
                    <th className="text-start px-4 py-3">{t("reports.services")}</th>
                    <th className="text-end px-4 py-3">{t("services.price")}</th>
                    <th className="text-end px-4 py-3">{t("ther.base")}</th>
                    <th className="text-end px-4 py-3">{t("ther.bonus5")}</th>
                    <th className="text-end px-4 py-3">{t("ther.bonus10")}</th>
                    <th className="text-end px-4 py-3">{t("reports.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-foreground/40">{t("common.empty")}</td></tr>}
                  {data.rows.map((r, i) => (
                    <tr key={r.tx_id ? `${r.tx_id}-${i}` : i} className="border-t border-charcoal-600/40 hover:bg-gold/5">
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.date)}</td>
                      <td className="px-4 py-3">{r.service}</td>
                      <td className="px-4 py-3 text-end">{formatSAR(r.price)}</td>
                      <td className="px-4 py-3 text-end">{formatSAR(r.commission_base)}</td>
                      <td className="px-4 py-3 text-end text-leaf/80">
                        {(r.commission_bonus_5 || 0) > 0 ? formatSAR(r.commission_bonus_5) : <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="px-4 py-3 text-end text-leaf">
                        {(r.commission_bonus_10 || 0) > 0 ? formatSAR(r.commission_bonus_10) : <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="px-4 py-3 text-end font-medium text-gold">{formatSAR(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {viewReceipt && (
        <PayoutReceipt
          payout={viewReceipt}
          therapist={{ name: user?.name, phone: user?.phone, id: user?.id }}
          onClose={() => setViewReceipt(null)}
        />
      )}
    </div>
  );
}
